import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TransferOutService } from '../../transfer-out-list/transfer-out.service';
import { TransferInService } from '../transfer-in.service';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-create-transfer-in',
  templateUrl: './create-transfer-in.component.html',
  imports: [CommonModule],
  styleUrl: './create-transfer-in.component.scss',
  standalone: true,
})
export class CreateTransferInComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private transferOutService = inject(TransferOutService);
  private transferInService = inject(TransferInService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private confirmDialog = inject(ConfirmDialogService);

  transferOutId: string | null = null;
  transferInId: string | null = null;
  transferOut: any = {};
  transferItems: any[] = [];

  totalQuantity: number = 0;
  grandTotal: number = 0;

  hideDraftBtn = false;
  viewOnly = false;

  transferInCreated = false;
  createdTransferInId = '';
  transferInNumber: string = '';
  ngOnInit(): void {
    this.viewOnly = this.route.snapshot.queryParamMap.get('view') === 'true';
    const path = this.route.snapshot.routeConfig?.path || '';
    const id = this.route.snapshot.paramMap.get('id');

    if (path.includes('edit')) {
      this.transferInId = id;
      if (this.transferInId) {
        this.loadTransferIn(this.transferInId);
        this.hideDraftBtn = true;
      }
    } else {
      this.transferOutId = id;
      if (this.transferOutId) {
        this.loadTransferOut(this.transferOutId);
        this.getNextTransferInNumber();
      }
    }
  }

  getNextTransferInNumber() {
    this.transferInService.getNextTransferInNumber().subscribe((res: any) => {
      if (res) {
        this.transferInNumber = res.transferInNumber;
      }
    });
  }

  loadTransferOut(id: string) {
    this.transferOutService.getTransferOutById(id).subscribe((res: any) => {
      this.transferOut = res;
      this.transferItems = res.items.map((item: any, i: number) => {
        const variant = item.productVariant || {};
        const product = variant.product || {};
        const pricing =
          Array.isArray(variant.pricing) && variant.pricing.length > 0
            ? variant.pricing[0]
            : {};
        this.grandTotal = Math.round(res.totalAmount) || res.totalAmount;
        this.totalQuantity = Math.round(res.totalQty) || res.totalQty;
        return {
          srNo: i + 1,
          id: item.id,
          productVariantId: item.productVariantId,
          sku: product.sku || '',
          description: variant.variantName || '',
          quantity: item.quantity,
          rate: pricing.costPrice ? +pricing.costPrice : null,
          mrp: pricing.mrp ? +pricing.mrp : null,
          netCost: pricing.sellingPrice ? +pricing.sellingPrice : null,
          basicCost: pricing.basePrice ? +pricing.basePrice : null,
          soh: variant.soh ?? null,
        };
      });
    });
  }

  loadTransferIn(id: string) {
    console.log(id);
    this.transferInService.getTransferInById(id).subscribe((res: any) => {
      this.transferOut = res;
      this.transferOut.transferOutNumber =
        res?.stockTransferOut?.transferOutNumber;

      this.transferOut.sourceLocation = res.stockTransferOut?.sourceLocation;
      this.transferOut.targetLocation = res.stockTransferOut?.targetLocation;
      this.transferItems = (res.items || []).map((item: any, i: number) => {
        const variant = item.productVariant || {};
        const product = variant.product || {};
        const pricing =
          Array.isArray(variant.pricing) && variant.pricing.length > 0
            ? variant.pricing[0]
            : {};

        return {
          srNo: i + 1,
          id: item.id,
          productVariantId: item.productVariantId,
          sku: product.sku || '',
          description: variant.variantName || '',
          quantity: item.quantityReceived ?? item.quantity,
          rate: pricing.costPrice ? +pricing.costPrice : null,
          mrp: pricing.mrp ? +pricing.mrp : null,
          netCost: pricing.sellingPrice ? +pricing.sellingPrice : null,
          basicCost: pricing.basePrice ? +pricing.basePrice : null,
          soh: variant.soh ?? null,
        };
      });
      this.transferInNumber = res.transferInNumber;
      this.createdTransferInId = res.id;
      this.transferInCreated = true;
      this.totalQuantity = res.totalQty;
      this.grandTotal = res.totalAmount;
    });
  }

  saveTransferIn() {
    const payload = {
      stockTransferOutId: this.transferOut.id,
      // The document number is assigned by the server at create. The number
      // shown on screen is a non-reserving preview, so it is not sent.
      indentId: this.transferOut.indentId || null,
      remarks: this.transferOut.remarks || '',
      totalQty: this.transferOut.totalQty,
      totalAmount: Number(this.transferOut.totalAmount),
      status: 'DRAFT',
      items: this.transferItems.map((item) => ({
        productVariantId: item.productVariantId,
        quantityReceived: item.quantity,
      })),
    };
    console.log(`Payload: ${JSON.stringify(payload)}`);

    this.transferInService.createTransferIn(payload).subscribe((res: any) => {
      this.toastr.success('Transfer In created successfully');
      this.transferInCreated = true;
      this.createdTransferInId = res?.id || res?.data?.id;
      if (res?.transferInNumber) this.transferInNumber = res.transferInNumber;
    });
  }

  async finalizeTransferIn() {
    if (!this.createdTransferInId) return;

    const proceed = await this.confirmDialog.confirm({
      title: 'Finalize Transfer In?',
      message:
        'Are you sure you want to finalize this Transfer In? Stock will be updated at the destination and this cannot be reversed.',
      confirmText: 'Finalize',
      cancelText: 'Cancel',
    });
    if (!proceed) return;

    this.transferInService
      .finalizeTransferIn(this.createdTransferInId)
      .subscribe(() => {
        this.router.navigate(['/transfer-in']);
        this.toastr.success('Transfer In finalized successfully');
      });
  }
  calculateTotals() {
    this.totalQuantity = this.transferOut.items.reduce(
      (sum: number, item: any) => {
        const quantity = item.quantityReceived || item.quantity || 0;
        return sum + quantity;
      },
      0
    );

    this.grandTotal = this.transferOut.items.reduce(
      (sum: number, item: any) => {
        const quantity = item.quantityReceived || item.quantity || 0;
        const costPrice = item.productVariant?.pricing[0]?.costPrice || 0;
        return sum + quantity * costPrice;
      },
      0
    );
  }

  // ── UI helpers for the Zenflow kit template (header toggle, text filter,
  //    line counts, per-line value). No business logic here. ──
  headerOpen = true;
  lineQuery = '';

  get lineCount(): number {
    return this.transferOut?.items?.length || 0;
  }

  /** Rows shown in the grid after the text filter, keeping the original index. */
  get visibleLines(): { item: any; index: number }[] {
    const q = (this.lineQuery || '').trim().toLowerCase();
    const out: { item: any; index: number }[] = [];
    (this.transferOut?.items || []).forEach((item: any, index: number) => {
      if (q) {
        const hay = `${item?.productVariant?.product?.sku || ''} ${item?.productVariant?.variantName || ''}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      out.push({ item, index });
    });
    return out;
  }

  get lineCountLabel(): string {
    const shown = this.visibleLines.length;
    const all = this.lineCount;
    const word = all === 1 ? 'line' : 'lines';
    return shown === all ? `${all} ${word}` : `${shown} of ${all} lines`;
  }

  /** Units dispatched by the source (received defaults to the same figure). */
  get totalSentQty(): number {
    return (this.transferOut?.items || []).reduce(
      (n: number, i: any) => n + (Number(i?.quantity) || 0),
      0
    );
  }

  /** Received quantity × the variant's cost price — the VALUE column. */
  lineValue(item: any): number {
    const qty = Number(item?.quantityReceived || item?.quantity || 0);
    const cost = Number(item?.productVariant?.pricing?.[0]?.costPrice || 0);
    return qty * cost;
  }
}
